import { memo } from 'react';
import { Flex, Button } from 'antd';
import clsx from 'clsx';
import { useAppSelector } from 'app/hooks';
import useIsMobile from 'hooks/useIsMobile';
import { useAdaChat } from 'contexts/AdaChatContext';
import If from 'components/If';
import LazySvg from 'components/LazySvg';
import { formatPhoneNumber } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import { selectBrandCustomerServicePhone } from 'services/brand/brandSelectors';
import { selectHasPhoneCustomerCareSupport } from 'services/plan/planSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { QaHeader, QaLoginPage } from 'testdata/constants';
import useHeaderRouteFlags from '../useHeaderRouteFlags';
import styles from './Menu.module.scss';

type TProps = {
  alignCenter?: boolean;
  className?: string;
};

function CustomerServiceHelp({ alignCenter = false, className = '' }: TProps) {
  const isMobile = useIsMobile();
  const customerServicePhone = useAppSelector(selectBrandCustomerServicePhone);
  const isUserLogin = useAppSelector(selectUserSignInStatus);
  const hasPhoneCustomerCareSupport = useAppSelector(
    selectHasPhoneCustomerCareSupport,
  );
  const { openAdaChat, isAdaChatAvailable } = useAdaChat();
  const { isSignInRoute, isPhoneSuppressedClient } = useHeaderRouteFlags();

  /* Public pages only — the member area is entitlement-driven and never consults these. */
  const showPhoneOnPublicPage =
    !isPhoneSuppressedClient &&
    !(isSignInRoute && getFromBrandConfig('hidePhoneOnSignIn'));

  const showServiceHelpNumber =
    !!customerServicePhone &&
    (isUserLogin ? hasPhoneCustomerCareSupport : showPhoneOnPublicPage);

  const wrapperClassName = clsx(styles.helperText, {
    [styles[className]]: className,
    [styles.alignCenter]: alignCenter,
  });
  const hasCustomerServiceSupport = getFromBrandConfig(
    'hasCustomerServiceSupport',
  );

  const handleOpenAdaChat = () => {
    openAdaChat('header');
  };

  const chatIcon = isMobile ? <LazySvg name='chat' aria-hidden /> : null;
  const callIcon = isMobile ? <LazySvg name='call' aria-hidden /> : null;

  return (
    <If condition={!!hasCustomerServiceSupport}>
      <Flex
        align='center'
        className={wrapperClassName}
        data-qa-selector={QaLoginPage.textCustomerSupport}
      >
        We&apos;re here to help{' '}
        <If condition={isAdaChatAvailable}>
          <Button
            type='link'
            className={styles.contactButton}
            icon={chatIcon}
            onClick={handleOpenAdaChat}
          >
            Chat with us
          </Button>
          <If condition={showServiceHelpNumber}> or call </If>
        </If>
        <If condition={showServiceHelpNumber}>
          <Button
            type='link'
            className={styles.contactButton}
            icon={callIcon}
            href={`tel:${customerServicePhone}`}
            data-qa-selector={QaHeader.linkCustomerSupportPhone}
          >
            {formatPhoneNumber(customerServicePhone)}
          </Button>
        </If>
      </Flex>
    </If>
  );
}

export default memo(CustomerServiceHelp);
